import { ColumnDef } from '@tanstack/react-table';
import { Badge } from 'components/ui/badge';
import { DataTableColumnHeader } from '../../../../components/blocks/data-table/data-table-column-header';
import { IamData } from '../../services/user-service';
import { Copy } from 'lucide-react';
import { GroupDataTableRowActions } from './iam-group-table-action';

/**
 * Creates the columns for the IAM (Identity and Access Management) table.
 *
 * This function generates the column configuration for a table displaying user data,
 * including user details like name, email, MFA status, account creation date, last login,
 * user status (active/inactive), and actions (view details, reset password, resend activation).
 *
 * Features:
 * - Displays user information with sorting and filtering capabilities
 * - Supports custom actions such as viewing details, resetting passwords, and resending activations
 * - Filters by date range for both creation and last login dates
 * - Displays user status with color-coded badges based on active/inactive status
 * - Displays MFA status with enabled/disabled labels
 *
 * @param {ColumnFactoryProps} props - The props for configuring the table columns
 * @param {function} props.onViewDetails - Callback function triggered when the "View Details" action is clicked
 * @param {function} props.onResetPassword - Callback function triggered when the "Reset Password" action is clicked
 * @param {function} [props.onResendActivation] - Optional callback function triggered when the "Resend Activation" action is clicked
 *
 * @returns {ColumnDef<IamData, any>[]} - An array of column definitions for the IAM table
 *
 * @example
 * const columns = createIamTableColumns({
 *   onViewDetails: (user) => console.log(user),
 *   onResetPassword: (user) => console.log('Resetting password for:', user),
 *   onResendActivation: (user) => console.log('Resending activation for:', user),
 * });
 */

type StatusOption = {
    value: string | boolean;
    label: string;
    color: 'success' | 'error';
};

const getUserStatuses = (t: (key: string) => string): StatusOption[] => [
    { value: 'active', label: t('ACTIVE'), color: 'success' },
    { value: 'inactive', label: t('INACTIVE'), color: 'error' },
];

interface ColumnFactoryProps {
    onResendActivation?: (user: IamData) => void;
    onDeactivateUser?: (user: IamData) => void;
    onChangePlan?: (user: IamData) => void;
    onCancelSubscription?: (user: IamData) => void;
    onCopyEmail?: (email: string) => void;
    onDelete?: (user: IamData) => void;
    t: (key: string) => string;
}

export const createGroupDetailsTableColumns = ({
    onCopyEmail,
    onDelete,
    t
}: ColumnFactoryProps): ColumnDef<IamData, any>[] => [
        {
            id: 'fullName',
            header: ({ column }) => <DataTableColumnHeader column={column} title={t('NAME')} />,
            accessorFn: (row) => {
                const fullName = `${row.firstName ?? ''} ${row.lastName ?? ''}`.trim();
                return fullName || '-';
            },
            cell: ({ row }) => {
                const fullName = `${row.original.firstName ?? ''} ${row.original.lastName ?? ''}`.trim();
                return (
                    <div className="flex items-center">
                        <span className="max-w-[300px] truncate font-medium">{fullName || '-'}</span>
                    </div>
                );
            },
        },
        {
            id: 'email',
            accessorFn: (row) => row.email,
            header: ({ column }) => <DataTableColumnHeader column={column} title={t('EMAIL')} />,
            cell: ({ row }) => (
                <div className="flex items-center gap-2 group">
                    <span className="max-w-[300px] truncate">{row.original.email}</span>
                    {onCopyEmail && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                onCopyEmail(row.original.email);
                            }}
                            className="flex items-center justify-center h-6 w-6 p-1 rounded hover:bg-muted transition-colors opacity-0 group-hover:opacity-100"
                            title={t('COPY_EMAIL')}
                        >
                            <Copy size={12} className="text-muted-foreground hover:text-foreground" />
                        </button>
                    )}
                </div>
            ),
        },
        {
            id: 'createdDate',
            accessorFn: (row) => row.createdDate,
            header: ({ column }) => <DataTableColumnHeader column={column} title={t('JOINED_ON')} />,
            cell: ({ row }) => {
                const date = new Date(row.original.createdDate);
                return (
                    <div className="flex items-center">
                        <span>
                            {new Date(date)
                                .toLocaleDateString('de-CH', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric',
                                })
                                .split('/')
                                .join('/')}
                        </span>
                    </div>
                );
            },
            sortingFn: (rowA, rowB) => {
                const a = new Date(rowA.original.createdDate).getTime();
                const b = new Date(rowB.original.createdDate).getTime();
                return a < b ? -1 : a > b ? 1 : 0;
            },
            filterFn: (row, id, value) => {
                if (!value?.from || !value?.to) return true;
                const rowDate = new Date(row.getValue(id));
                return rowDate >= value.from && rowDate <= value.to;
            },
        },
        {
            id: 'active',
            accessorFn: (row) => row.active,
            header: ({ column }) => <DataTableColumnHeader column={column} title={t('STATUS')} />,
            cell: ({ row }) => {
                const status = getUserStatuses(t).find(
                    (status) => status.value === (row.original.active ? 'active' : 'inactive')
                );
                if (!status) return null;
                return (
                    <div className="flex items-center">
                        <Badge
                            variant="outline"
                            className={status.color === 'success' ? 'text-success' : 'text-error'}
                        >
                            {status.label}
                        </Badge>
                    </div>
                );
            },
            sortingFn: (rowA, rowB, columnId) => {
                const a = rowA.getValue(columnId);
                const b = rowB.getValue(columnId);

                if (b === a) return 0;
                return a ? -1 : 1;
            },
            filterFn: (row, id, value: string[]) => {
                if (value.length === 0) return true;
                const cellValue = row.getValue(id);
                return value.includes(String(cellValue));
            },
        },
        {
            id: 'actions',
            cell: ({ row }) => (
                <GroupDataTableRowActions
                    row={row}
                    onDelete={onDelete}

                />
            ),
        },
    ];
